// ============================================================
// Square Code Assistants — the owner's governed agent supervision surface.
//
// Registered at /square/command-center/assistants (+ /tasks/:taskId). Drives
// the REAL agent runtime: pick an agent + project + instruction, create a
// durable task, watch its live event stream, approve gated writes, and
// pause/cancel/retry. Registry truth (no fixture roster); every control is
// honest about provider/capability state.
//
// Inline styles referencing @square/ui tokens per the shell convention.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../api/client'

type Rec = Record<string, unknown>
const s = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d)
const n = (v: unknown): number | null => (typeof v === 'number' ? v : null)

const MODES = [
  { v: 'advise', label: 'Advise (read-only)' },
  { v: 'supervised', label: 'Supervised (writes need approval)' },
  { v: 'draft', label: 'Draft only' },
]

const TERMINAL = new Set(['completed', 'completed_with_warnings', 'failed', 'cancelled'])

const statusColor: Record<string, string> = {
  completed: 'var(--sc-green)', completed_with_warnings: 'var(--sc-orange)',
  failed: 'var(--sc-red)', cancelled: 'var(--sc-text-muted)',
  awaiting_approval: 'var(--sc-orange)', running: 'var(--sc-cyan)', planning: 'var(--sc-cyan)',
  paused: 'var(--sc-text-tertiary)', queued: 'var(--sc-text-tertiary)', blocked: 'var(--sc-red)',
}

const panel: React.CSSProperties = {
  background: 'var(--sc-surface-2)', border: '1px solid var(--sc-border-rest)',
  borderRadius: 'var(--sc-radius-lg)', padding: 16,
}

export default function SquareCodeAssistantsRoute() {
  const navigate = useNavigate()
  const { taskId: routeTaskId } = useParams<{ taskId?: string }>()

  const [agents, setAgents] = useState<Rec[]>([])
  const [providerReady, setProviderReady] = useState<boolean | null>(null)
  const [health, setHealth] = useState<Rec | null>(null)
  const [selAgent, setSelAgent] = useState<string>('')
  const [projectId, setProjectId] = useState('')
  const [instruction, setInstruction] = useState('')
  const [title, setTitle] = useState('')
  const [mode, setMode] = useState('supervised')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [activeTask, setActiveTask] = useState<string | null>(routeTaskId ?? null)
  const [task, setTask] = useState<Rec | null>(null)
  const [events, setEvents] = useState<Rec[]>([])
  const [pending, setPending] = useState<Rec | null>(null)

  useEffect(() => {
    api.agentRuntime.agents.list()
      .then((r) => { setAgents(r.agents); setProviderReady(r.provider_configured); if (!selAgent && r.agents[0]) setSelAgent(s(r.agents[0].agent_id)) })
      .catch(() => setError('Could not load the agent roster.'))
    api.agentRuntime.health().then((r) => setHealth(r as unknown as Rec)).catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async (id: string) => {
    try {
      const [t, e] = await Promise.all([api.agentRuntime.tasks.get(id), api.agentRuntime.events(id)])
      setTask(t.task); setPending(t.pending_approval); setEvents(e.events)
    } catch { /* transient */ }
  }, [])

  useEffect(() => { if (activeTask) void refresh(activeTask) }, [activeTask, refresh])

  // Poll while a task is live.
  useEffect(() => {
    if (!activeTask || !task) return
    if (TERMINAL.has(s(task.runtime_status))) return
    const h = setInterval(() => void refresh(activeTask), 2500)
    return () => clearInterval(h)
  }, [activeTask, task, refresh])

  const createTask = useCallback(async () => {
    setError(null); setBusy(true)
    try {
      const r = await api.agentRuntime.tasks.create({
        agent_id: selAgent, title: title || instruction.slice(0, 60) || 'Agent task',
        instruction, project_id: projectId || undefined, execution_mode: mode,
      })
      setActiveTask(r.task_id); navigate(`/square/command-center/assistants/tasks/${r.task_id}`)
      await api.agentRuntime.tick(r.task_id).catch(() => {})
      await refresh(r.task_id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the task.')
    } finally { setBusy(false) }
  }, [selAgent, title, instruction, projectId, mode, navigate, refresh])

  const runTick = useCallback(async () => {
    if (!activeTask) return
    setBusy(true)
    try { await api.agentRuntime.tick(activeTask); await refresh(activeTask) } finally { setBusy(false) }
  }, [activeTask, refresh])

  const decide = useCallback(async (decision: 'approved' | 'rejected') => {
    if (!activeTask || !pending) return
    setBusy(true)
    try {
      await api.agentRuntime.approve(s(pending.id), decision)
      await api.agentRuntime.tick(activeTask)
      await refresh(activeTask)
    } catch (e) { setError(e instanceof Error ? e.message : 'Approval failed.') } finally { setBusy(false) }
  }, [activeTask, pending, refresh])

  const control = useCallback(async (action: 'pause' | 'resume' | 'cancel' | 'retry') => {
    if (!activeTask) return
    setBusy(true)
    try { await api.agentRuntime.control(activeTask, action); if (action === 'resume' || action === 'retry') await api.agentRuntime.tick(activeTask); await refresh(activeTask) } finally { setBusy(false) }
  }, [activeTask, refresh])

  const status = s(task?.runtime_status)
  const plan = (task?.public_plan as Rec | undefined)?.summary

  return (
    <div style={{ padding: 24, color: 'var(--sc-text-primary)', fontFamily: 'var(--sc-font-ui)', display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: 20, alignItems: 'start' }}>
      {/* ── Left: roster + composer ── */}
      <div style={{ display: 'grid', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--sc-font-display)', fontSize: 22, margin: '0 0 4px' }}>Assistants</h1>
          <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13, margin: 0 }}>Governed agents that do real, project-scoped work under your supervision.</p>
        </div>

        {providerReady === false && (
          <div style={{ ...panel, border: '1px solid var(--sc-orange)', background: 'var(--sc-surface-3)' }}>
            <strong style={{ color: 'var(--sc-orange)' }}>Model provider not configured.</strong>
            <p style={{ fontSize: 12, color: 'var(--sc-text-secondary)', margin: '6px 0 0' }}>Agents will plan and gate writes, but cannot make live model calls until <code>ANTHROPIC_API_KEY</code> is set on the backend and redeployed.</p>
          </div>
        )}

        <div style={{ display: 'grid', gap: 8 }}>
          {agents.map((a) => {
            const id = s(a.agent_id)
            const on = id === selAgent
            return (
              <button key={id} onClick={() => setSelAgent(id)} style={{
                ...panel, textAlign: 'left', cursor: 'pointer',
                borderColor: on ? 'var(--sc-border-active)' : 'var(--sc-border-rest)',
                background: on ? 'var(--sc-surface-3)' : 'var(--sc-surface-2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong style={{ fontFamily: 'var(--sc-font-display)' }}>{s(a.display_name, id)}</strong>
                  <span style={{ fontSize: 11, color: s(a.availability) === 'available' ? 'var(--sc-green)' : 'var(--sc-orange)' }}>{s(a.availability, s(a.status))}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--sc-text-secondary)', marginTop: 4 }}>{s(a.description, s(a.role))}</div>
                <div style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 6, fontFamily: 'var(--sc-font-mono)' }}>{s(a.model)}</div>
              </button>
            )
          })}
          {agents.length === 0 && <div style={{ ...panel, color: 'var(--sc-text-tertiary)', fontSize: 13 }}>No governed agents seeded yet. Apply migration 20260721000001.</div>}
        </div>

        <div style={{ ...panel, display: 'grid', gap: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>Instruction
            <textarea value={instruction} onChange={(e) => setInstruction(e.target.value)} rows={3} placeholder="e.g. Summarize the health of this project." style={inputStyle} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>Project id (optional)
            <input value={projectId} onChange={(e) => setProjectId(e.target.value)} placeholder="sq_projects.id" style={inputStyle} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--sc-text-secondary)' }}>Execution mode
            <select value={mode} onChange={(e) => setMode(e.target.value)} style={inputStyle}>
              {MODES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </label>
          <button onClick={createTask} disabled={busy || !selAgent || !instruction.trim()} style={primaryBtn(busy || !selAgent || !instruction.trim())}>
            {busy ? 'Working…' : 'Start task'}
          </button>
          {error && <div style={{ color: 'var(--sc-red)', fontSize: 12 }}>{error}</div>}
        </div>
      </div>

      {/* ── Right: active task supervision ── */}
      <div style={{ display: 'grid', gap: 16 }}>
        {!activeTask && <div style={{ ...panel, color: 'var(--sc-text-tertiary)' }}>Select an agent, describe the task, and press <strong>Start task</strong>. The agent publishes a plan, runs read tools, and stops for your approval before any write.</div>}

        {activeTask && (
          <>
            <div style={{ ...panel, display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontFamily: 'var(--sc-font-display)' }}>{s(task?.title, 'Task')}</strong>
                <span style={{ fontSize: 12, color: statusColor[status] ?? 'var(--sc-text-secondary)', fontFamily: 'var(--sc-font-mono)' }}>{status || '…'}</span>
              </div>
              {plan != null && <div style={{ fontSize: 13, color: 'var(--sc-text-secondary)', whiteSpace: 'pre-wrap', borderLeft: '2px solid var(--sc-border-active)', paddingLeft: 10 }}>{s(plan)}</div>}
              {s(task?.final_summary) && <div style={{ fontSize: 13 }}><strong>Result:</strong> {s(task?.final_summary)}</div>}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                {!TERMINAL.has(status) && <button onClick={runTick} disabled={busy} style={ghostBtn}>Continue</button>}
                {!TERMINAL.has(status) && status !== 'paused' && <button onClick={() => control('pause')} disabled={busy} style={ghostBtn}>Pause</button>}
                {status === 'paused' && <button onClick={() => control('resume')} disabled={busy} style={ghostBtn}>Resume</button>}
                {!TERMINAL.has(status) && <button onClick={() => control('cancel')} disabled={busy} style={ghostBtn}>Cancel</button>}
                {(status === 'failed' || status === 'blocked' || status === 'completed_with_warnings') && <button onClick={() => control('retry')} disabled={busy} style={ghostBtn}>Retry</button>}
              </div>
            </div>

            {pending && (
              <div style={{ ...panel, border: '1px solid var(--sc-orange)' }}>
                <strong style={{ color: 'var(--sc-orange)' }}>Approval required</strong>
                <div style={{ fontSize: 13, margin: '6px 0' }}>{s(pending.summary)}</div>
                <pre style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', background: 'var(--sc-surface-1)', padding: 8, borderRadius: 'var(--sc-radius-sm)', overflow: 'auto' }}>{JSON.stringify(pending.proposed_payload ?? {}, null, 2)}</pre>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => decide('approved')} disabled={busy} style={primaryBtn(busy)}>Approve</button>
                  <button onClick={() => decide('rejected')} disabled={busy} style={ghostBtn}>Reject</button>
                </div>
                <p style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', marginTop: 6 }}>Deciding requires operator authority. The agent executes only after you approve, exactly once, verified by re-read.</p>
              </div>
            )}

            <div style={{ ...panel }}>
              <strong style={{ fontSize: 13 }}>Event timeline</strong>
              <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                {events.map((e) => (
                  <div key={s(e.id)} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, fontSize: 12, borderBottom: '1px solid var(--sc-border-rest)', paddingBottom: 6 }}>
                    <span style={{ fontFamily: 'var(--sc-font-mono)', color: 'var(--sc-text-tertiary)' }}>{s(e.event_type)}</span>
                    <span style={{ color: 'var(--sc-text-secondary)' }}>{s(e.message)}</span>
                  </div>
                ))}
                {events.length === 0 && <span style={{ color: 'var(--sc-text-tertiary)', fontSize: 12 }}>No events yet.</span>}
              </div>
            </div>
          </>
        )}

        {health && (
          <div style={{ ...panel, fontSize: 12 }}>
            <strong>Runtime health: </strong><span style={{ color: s((health as Rec).overall) === 'healthy' ? 'var(--sc-green)' : 'var(--sc-orange)' }}>{s((health as Rec).overall)}</span>
            <div style={{ marginTop: 6, display: 'grid', gap: 3 }}>
              {Object.entries(((health as Rec).checks ?? {}) as Record<string, { state: string; detail: string }>).map(([k, v]) => (
                <div key={k} style={{ color: 'var(--sc-text-tertiary)' }}><span style={{ fontFamily: 'var(--sc-font-mono)' }}>{k}</span>: {v.state} — {v.detail}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', marginTop: 4, padding: '8px 10px', background: 'var(--sc-surface-1)',
  border: '1px solid var(--sc-border-rest)', borderRadius: 'var(--sc-radius-sm)',
  color: 'var(--sc-text-primary)', fontFamily: 'var(--sc-font-ui)', fontSize: 13, boxSizing: 'border-box',
}
function primaryBtn(disabled: boolean): React.CSSProperties {
  return { padding: '9px 14px', background: disabled ? 'var(--sc-surface-4)' : 'var(--sc-cyan)', color: disabled ? 'var(--sc-text-tertiary)' : '#05070f', border: 'none', borderRadius: 'var(--sc-radius-sm)', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13 }
}
const ghostBtn: React.CSSProperties = {
  padding: '8px 12px', background: 'transparent', color: 'var(--sc-text-primary)',
  border: '1px solid var(--sc-border-hover)', borderRadius: 'var(--sc-radius-sm)', cursor: 'pointer', fontSize: 12,
}
